import React, { useState } from 'react';
import { 
  CreditCard, 
  DollarSign, 
  Plus, 
  Trash2, 
  Edit3, 
  Check, 
  X,
  AlertCircle,
  Banknote,
  Wallet,
  Building2,
  Smartphone
} from 'lucide-react';

const PaymentMethodsSettings = ({ 
  settings, 
  handleSettingChange, 
  currentCompany 
}) => {
  const [editingMethodId, setEditingMethodId] = useState(null);
  const [editingMethodData, setEditingMethodData] = useState({});
  const [addingNewMethod, setAddingNewMethod] = useState(false);
  const [newMethodData, setNewMethodData] = useState({
    name: '',
    type: 'cash',
    enabled: true,
    icon: 'DollarSign',
    description: '',
    isDefault: false
  });

  // Get payment methods safely
  const getPaymentMethods = () => {
    return Array.isArray(settings.paymentMethods) ? settings.paymentMethods : [];
  };

  // Get icon component based on type
  const getPaymentIcon = (type) => {
    switch (type) {
      case 'cash':
        return DollarSign;
      case 'credit_card':
        return CreditCard;
      case 'debit_card':
        return CreditCard;
      case 'bank_transfer':
        return Building2;
      case 'mobile_payment':
        return Smartphone;
      case 'wallet':
        return Wallet;
      default:
        return Banknote;
    }
  };

  // Handle enable/disable payment method
  const handleToggleMethod = (methodId) => {
    const methods = getPaymentMethods();
    const updatedMethods = methods.map(method =>
      method.id === methodId
        ? { ...method, enabled: !method.enabled }
        : method
    );
    handleSettingChange('paymentMethods', updatedMethods);
  };

  // Handle set as default
  const handleSetDefault = (methodId) => {
    const methods = getPaymentMethods();
    const updatedMethods = methods.map(method => ({
      ...method,
      isDefault: method.id === methodId
    }));
    handleSettingChange('paymentMethods', updatedMethods);
  };

  // Handle delete payment method
  const handleDeleteMethod = (methodId) => {
    const methods = getPaymentMethods();
    const method = methods.find(m => m.id === methodId);
    
    if (method?.isDefault && methods.length > 1) {
      alert('Cannot delete the default payment method. Set another method as default first.');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete "${method?.name}"?`)) {
      const updatedMethods = methods.filter(m => m.id !== methodId);
      handleSettingChange('paymentMethods', updatedMethods);
    }
  };

  // Start editing payment method
  const startEditingMethod = (method) => {
    setEditingMethodId(method.id);
    setEditingMethodData({ ...method });
  };

  // Save edited payment method
  const saveEditedMethod = () => {
    if (!editingMethodData.name.trim()) {
      alert('Payment method name is required');
      return;
    }

    const methods = getPaymentMethods();
    const updatedMethods = methods.map(method =>
      method.id === editingMethodId ? { ...editingMethodData } : method
    );
    
    handleSettingChange('paymentMethods', updatedMethods);
    setEditingMethodId(null);
    setEditingMethodData({});
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingMethodId(null);
    setEditingMethodData({});
  };

  // Add new payment method
  const handleAddNewMethod = () => {
    if (!newMethodData.name.trim()) {
      alert('Payment method name is required');
      return;
    }

    const methods = getPaymentMethods();
    const newId = methods.length > 0 ? Math.max(...methods.map(m => m.id)) + 1 : 1;
    
    const newMethod = {
      ...newMethodData,
      id: newId
    };

    // If this is the first method or marked as default, set it as default
    if (methods.length === 0 || newMethodData.isDefault) {
      const updatedMethods = methods.map(m => ({ ...m, isDefault: false }));
      handleSettingChange('paymentMethods', [...updatedMethods, newMethod]);
    } else {
      handleSettingChange('paymentMethods', [...methods, newMethod]);
    }

    // Reset form
    setNewMethodData({
      name: '',
      type: 'cash',
      enabled: true,
      icon: 'DollarSign',
      description: '',
      isDefault: false
    });
    setAddingNewMethod(false);
  };

  // Cancel adding new method
  const cancelAddingNewMethod = () => {
    setNewMethodData({
      name: '',
      type: 'cash',
      enabled: true,
      icon: 'DollarSign',
      description: '',
      isDefault: false
    });
    setAddingNewMethod(false);
  };

  const methods = getPaymentMethods();
  const enabledMethods = methods.filter(m => m.enabled);
  const defaultMethod = methods.find(m => m.isDefault);

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-lg font-semibold text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-orange-400" />
              Payment Methods Configuration
            </h4>
            <p className="text-gray-400 text-sm mt-1">
              Configure available payment methods for reservations
            </p>
          </div>
          <button
            onClick={() => setAddingNewMethod(true)}
            className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-600 text-white px-4 py-2 rounded-lg hover:from-orange-600 hover:to-red-700 transition-colors"
          >
            <Plus size={16} />
            <span>Add Payment Method</span>
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="p-6 border-b border-gray-700 bg-gray-700/30">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gray-800/50 rounded-lg p-4">
            <div className="text-gray-400 text-sm mb-1">Total Methods</div>
            <div className="text-2xl font-bold text-white">{methods.length}</div>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-4">
            <div className="text-gray-400 text-sm mb-1">Enabled</div>
            <div className="text-2xl font-bold text-green-400">{enabledMethods.length}</div>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-4">
            <div className="text-gray-400 text-sm mb-1">Disabled</div>
            <div className="text-2xl font-bold text-red-400">{methods.length - enabledMethods.length}</div>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-4">
            <div className="text-gray-400 text-sm mb-1">Default Method</div>
            <div className="text-lg font-bold text-blue-400">{defaultMethod?.name || 'None'}</div>
          </div>
        </div>
      </div>

      {/* Add New Payment Method Form */}
      {addingNewMethod && (
        <div className="p-6 border-b border-gray-700 bg-blue-900/20">
          <h5 className="text-white font-medium mb-4 flex items-center gap-2">
            <Plus size={16} className="text-blue-400" />
            Add New Payment Method
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Method Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newMethodData.name}
                onChange={(e) => setNewMethodData({ ...newMethodData, name: e.target.value })}
                placeholder="e.g., Credit Card, Cash, Bank Transfer"
                className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Method Type
              </label>
              <select
                value={newMethodData.type}
                onChange={(e) => setNewMethodData({ ...newMethodData, type: e.target.value })}
                className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="cash">Cash</option>
                <option value="credit_card">Credit Card</option>
                <option value="debit_card">Debit Card</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="mobile_payment">Mobile Payment</option>
                <option value="wallet">Digital Wallet</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description (Optional)
              </label>
              <input
                type="text"
                value={newMethodData.description}
                onChange={(e) => setNewMethodData({ ...newMethodData, description: e.target.value })}
                placeholder="Brief description of this payment method"
                className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={newMethodData.enabled}
                  onChange={(e) => setNewMethodData({ ...newMethodData, enabled: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-gray-300 text-sm">Enabled</span>
              </label>

              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={newMethodData.isDefault}
                  onChange={(e) => setNewMethodData({ ...newMethodData, isDefault: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-gray-300 text-sm">Set as Default</span>
              </label>
            </div>
          </div>

          <div className="flex items-center gap-3 mt-4">
            <button
              onClick={handleAddNewMethod}
              className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 rounded-lg hover:from-green-600 hover:to-green-700 transition-colors"
            >
              <Check size={16} />
              <span>Add Method</span>
            </button>
            <button
              onClick={cancelAddingNewMethod}
              className="flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              <X size={16} />
              <span>Cancel</span>
            </button>
          </div>
        </div>
      )}

      {/* Payment Methods List */}
      <div className="p-6">
        {methods.length === 0 ? (
          <div className="text-center py-12">
            <CreditCard className="mx-auto h-16 w-16 text-gray-600 mb-4" />
            <p className="text-gray-400 text-lg mb-2">No Payment Methods Configured</p>
            <p className="text-gray-500 text-sm mb-4">
              Add payment methods to allow customers to select their preferred payment option
            </p>
            <button
              onClick={() => setAddingNewMethod(true)}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-600 text-white px-4 py-2 rounded-lg hover:from-orange-600 hover:to-red-700 transition-colors"
            >
              <Plus size={16} />
              <span>Add First Payment Method</span>
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {methods.map((method) => {
              const IconComponent = getPaymentIcon(method.type);
              const isEditing = editingMethodId === method.id;

              if (isEditing) {
                return (
                  <div key={method.id} className="bg-blue-900/20 border-2 border-blue-500 rounded-lg p-4">
                    <h5 className="text-white font-medium mb-3 flex items-center gap-2">
                      <Edit3 size={14} className="text-blue-400" />
                      Edit Payment Method
                    </h5>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Method Name</label>
                        <input
                          type="text"
                          value={editingMethodData.name}
                          onChange={(e) => setEditingMethodData({ ...editingMethodData, name: e.target.value })}
                          className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Type</label>
                        <select
                          value={editingMethodData.type}
                          onChange={(e) => setEditingMethodData({ ...editingMethodData, type: e.target.value })}
                          className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded text-sm"
                        >
                          <option value="cash">Cash</option>
                          <option value="credit_card">Credit Card</option>
                          <option value="debit_card">Debit Card</option>
                          <option value="bank_transfer">Bank Transfer</option>
                          <option value="mobile_payment">Mobile Payment</option>
                          <option value="wallet">Digital Wallet</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Description</label>
                        <input
                          type="text"
                          value={editingMethodData.description || ''}
                          onChange={(e) => setEditingMethodData({ ...editingMethodData, description: e.target.value })}
                          className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded text-sm"
                        />
                      </div>
                      <div className="flex items-center gap-3 pt-2">
                        <button
                          onClick={saveEditedMethod}
                          className="flex items-center gap-1 bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 transition-colors text-sm"
                        >
                          <Check size={14} />
                          <span>Save</span>
                        </button>
                        <button
                          onClick={cancelEditing}
                          className="flex items-center gap-1 bg-gray-600 text-white px-3 py-1 rounded hover:bg-gray-700 transition-colors text-sm"
                        >
                          <X size={14} />
                          <span>Cancel</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={method.id}
                  className={`rounded-lg p-4 border-2 transition-all ${
                    method.enabled
                      ? method.isDefault
                        ? 'border-blue-500 bg-blue-900/20'
                        : 'border-green-500/50 bg-gray-700/30'
                      : 'border-gray-600 bg-gray-700/20 opacity-60'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        method.enabled ? 'bg-orange-500/20' : 'bg-gray-600/20'
                      }`}>
                        <IconComponent className={`w-5 h-5 ${method.enabled ? 'text-orange-400' : 'text-gray-500'}`} />
                      </div>
                      <div>
                        <div className="font-bold text-white flex items-center gap-2">
                          {method.name}
                          {method.isDefault && (
                            <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                              Default
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-400 capitalize">
                          {method.type.replace('_', ' ')}
                        </div>
                      </div>
                    </div>
                  </div>

                  {method.description && (
                    <p className="text-xs text-gray-400 mb-3">{method.description}</p>
                  )}

                  <div className="flex items-center justify-between pt-3 border-t border-gray-600">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleMethod(method.id)}
                        className={`text-xs px-3 py-1 rounded-full transition-colors ${
                          method.enabled
                            ? 'bg-green-900/30 text-green-400 hover:bg-green-900/50'
                            : 'bg-red-900/30 text-red-400 hover:bg-red-900/50'
                        }`}
                      >
                        {method.enabled ? 'Enabled' : 'Disabled'}
                      </button>
                      {!method.isDefault && method.enabled && (
                        <button
                          onClick={() => handleSetDefault(method.id)}
                          className="text-xs px-3 py-1 rounded-full bg-blue-900/30 text-blue-400 hover:bg-blue-900/50 transition-colors"
                        >
                          Set Default
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => startEditingMethod(method)}
                        className="p-1.5 text-blue-400 hover:bg-blue-900/30 rounded transition-colors"
                        title="Edit"
                      >
                        <Edit3 size={14} />
                      </button>
                      <button
                        onClick={() => handleDeleteMethod(method.id)}
                        className="p-1.5 text-red-400 hover:bg-red-900/30 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Info Panel */}
      <div className="p-6 bg-gray-700/30 border-t border-gray-700">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-300">
            <p className="font-medium text-white mb-2">Payment Method Configuration Tips:</p>
            <ul className="space-y-1 text-gray-400 list-disc list-inside">
              <li>Enable/disable methods to control what customers can select</li>
              <li>Set a default method to pre-select for new reservations</li>
              <li>Add descriptions to help customers understand each payment option</li>
              <li>At least one payment method must be enabled</li>
              <li>Payment methods are company-specific and isolated from other companies</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodsSettings;